import { Injectable } from '@angular/core';
import { ApolloCache, DataProxy } from '@apollo/client/core';
import { removeByGid, replace } from '@models/apollo';
import {
  CreateLicenseInput,
  CreateLicenseMutation,
  License,
  LicenseGroup,
  LicenseUse,
  LoadEnrolmentKeysQuery,
  LoadLicenseGroupDetailsQuery,
  LoadLicenseGroupListQuery,
  LoadLicenseInvitesQuery,
  LoadLicenseOrGroupForDistributionQuery,
  LoadLicenseUsageQuery,
  ProvideSupportLicensesDocument,
  ProvideSupportLicensesQueryResult,
  ProvideSupportLicensesQueryVariables,
  ShareLicenseInput,
  ShareLicenseMutation,
  SupportLicense,
  TakeCourseInput,
  TakeCourseMutation,
  UpdateLicenseInput,
  UpdateLicenseMutation
} from '@queries/generated';
import { finalMap } from '@rx/final-map';
import { GlobalID } from '@util/global-id';
import { map, tap } from 'rxjs/operators';

interface CreateLicenseOptions {
  refetchQueries?: string[];
  update?: (store: ApolloCache<any>, license: License) => void;
}

const PER_PAGE = 100;

@Injectable({
  providedIn: 'root'
})
export class LicensesService {
  private licenseGroupListQuery = this.loadLicenseGroupListQuery.watch({ first: PER_PAGE });

  get licenseGroups$() {
    let nodesLength = 0;
    return this.licenseGroupListQuery.valueChanges.pipe(
      map(({ data }) => data.licenseGroups.all),
      tap(({ pageInfo, nodes }) => {
        // not checking pageInfo.hasNextPage, because it causes an extra query
        // so we skip it in this case in favor of just checking the nodes.length
        // if we loaded exactly PER_PAGE nodes, we fetch more assuming there are more to come
        // use % because nodes will be the total nodes loaded so far, not the current page
        // also check if nodes.length > nodesLength to prevent infinite loop in case the
        // user has exactly PER_PAGE license groups
        if (nodes.length % PER_PAGE == 0 && nodes.length > nodesLength) {
          const after = pageInfo.endCursor;
          nodesLength = nodes.length;
          this.licenseGroupListQuery.fetchMore({ variables: { first: PER_PAGE, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  }

  constructor(
    private loadLicenseGroupListQuery: LoadLicenseGroupListQuery,
    private loadLicenseOrGroupForDistributionQuery: LoadLicenseOrGroupForDistributionQuery,
    private loadEnrolmentKeysQuery: LoadEnrolmentKeysQuery,
    private loadLicenseUsageQuery: LoadLicenseUsageQuery,
    private loadLicenseInvitesQuery: LoadLicenseInvitesQuery,
    private loadLicenseGroupDetailsQuery: LoadLicenseGroupDetailsQuery,
    private updateLicenseMutation: UpdateLicenseMutation,
    private shareLicenseMutation: ShareLicenseMutation,
    private createLicenseMutation: CreateLicenseMutation,
    private takeCourseMutation: TakeCourseMutation
  ) {}

  loadLicenseOrLicenseGroupForDistribution = (gid: string) => {
    return (
      this.loadLicenseOrGroupForDistributionQuery
        .watch({ gid })
        // Using node entrypoint, have to cast
        .valueChanges.pipe(map(({ data }) => data.node as License | LicenseGroup))
    );
  };

  loadEnrolmentKeys = (gid: string) => {
    const query = this.loadEnrolmentKeysQuery.watch({ gid });
    return query.valueChanges.pipe(
      map(({ data }) => {
        const node = data.node as License | LicenseGroup;
        return node.enrolmentKeys;
      }),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          query.fetchMore({ variables: { gid, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  };

  loadUsage = (gid: string) => {
    return this.loadLicenseUsageQuery.watch({ gid }).valueChanges.pipe(
      map(({ data }) => {
        const node = data.node as License | LicenseGroup;
        return node.uses;
      })
    );
  };

  loadInvites = (gid: string) => {
    const variables = { gid };
    const query = this.loadLicenseInvitesQuery.watch(variables);
    return query.valueChanges.pipe(
      map(({ data }) => {
        const node = data.node as License | LicenseGroup;
        return node.invites;
      }),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          query.fetchMore({ variables: { ...variables, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  };

  licenseGroupDetails = (gid: string) => {
    const query = this.loadLicenseGroupDetailsQuery.watch({ gid });
    return query.valueChanges.pipe(
      map(({ data }) => data.licenseGroups.findByGid.licenses),
      tap(({ pageInfo }) => {
        if (pageInfo.hasNextPage) {
          const after = pageInfo.endCursor;
          query.fetchMore({ variables: { gid, after } });
        }
      }),
      map(({ nodes }) => nodes)
    );
  };

  // Provide Support is the only place you can currently revoke/destroy licenses, so this is specific to that right now.
  removeFromCache(store: DataProxy, license: SupportLicense, supportedUserId: number) {
    const variables = {
      id: supportedUserId
    };
    const { provideSupport } = store.readQuery<ProvideSupportLicensesQueryResult, ProvideSupportLicensesQueryVariables>(
      {
        query: ProvideSupportLicensesDocument,
        variables
      }
    );
    const data = {
      provideSupport: {
        ...provideSupport,
        licenses: {
          ...provideSupport.licenses,
          nodes: provideSupport.licenses.nodes.filter((l) => l.gid !== license.gid)
        }
      }
    };
    store.writeQuery<ProvideSupportLicensesQueryResult, ProvideSupportLicensesQueryVariables>({
      query: ProvideSupportLicensesDocument,
      variables,
      data
    });
  }

  removeUseFromCache(store: ApolloCache<any>, license: License | LicenseGroup | SupportLicense, use: LicenseUse) {
    // @ts-ignore
    // need license to come from type License or it looks up a support license which does not have uses
    const gid = new GlobalID({ model: 'License', id: license.id });
    store.modify({
      id: gid.toApolloCacheKey(),
      fields: {
        uses: removeByGid(use.gid)
      }
    });
  }

  update(input: UpdateLicenseInput) {
    return this.updateLicenseMutation
      .mutate(
        { input },
        {
          update: (store, { data: { updateLicense } }) => {
            if (updateLicense.errors.length > 0) {
              return;
            }

            const gid = new GlobalID({ model: 'SupportLicense', id: updateLicense.license.id });
            store.modify({
              id: gid.toApolloCacheKey(),
              fields: {
                seats: replace(updateLicense.license.seats),
                availableSeats: replace(updateLicense.license.availableSeats)
              }
            });
          }
        }
      )
      .pipe(
        map(({ data }) => data.updateLicense),
        finalMap('license')
      );
  }

  shareLicense(input: ShareLicenseInput) {
    return this.shareLicenseMutation
      .mutate(
        { input },
        {
          refetchQueries: ['LoadLicenseGroupList', 'LoadLicenseGroupDetails']
        }
      )
      .pipe(
        map(({ data }) => data.shareLicense),
        finalMap('sharedLicense')
      );
  }

  createLicense(input: CreateLicenseInput, options?: CreateLicenseOptions) {
    return this.createLicenseMutation
      .mutate(
        { input },
        {
          refetchQueries: options?.refetchQueries
          // Saving for future use
          // update: (store, { data: { createLicense } }) => {
          //   if (createLicense.errors.length > 0) {
          //     return;
          //   }

          //   if (options.update) {
          //     options.update(store, createLicense.license);
          //   }
          // }
        }
      )
      .pipe(
        map(({ data }) => data.createLicense),
        finalMap('license')
      );
  }

  takeCourse = (input: TakeCourseInput) => {
    return this.takeCourseMutation
      .mutate(
        { input },
        {
          refetchQueries: ['LoadLearnerCourseList', 'LoadLicenseGroupListQuery']
        }
      )
      .pipe(
        map(({ data }) => data.takeCourse),
        finalMap('grant')
      );
  };
}
